/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useGetWorkerShift } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import {
  type GetComparisonPairResponse,
  useGetComparisonPair,
} from "@src/appV2/redesign/Workplace/Review/api/useGetComparisonPair";
import { useGetQuestions } from "@src/appV2/redesign/Workplace/Review/api/useGetQuestions";
import { usePostRating } from "@src/appV2/redesign/Workplace/Review/api/usePostRating";
import {
  type PostReviewRequest,
  usePostReview,
} from "@src/appV2/redesign/Workplace/Review/api/usePostReview";
import { usePostWorkplaceComparison } from "@src/appV2/redesign/Workplace/Review/api/usePostWorkplaceComparison";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";

import { type TimeSlot } from "../redesign/Shift/Open/types";
import { ReviewQuestionType } from "../redesign/Workplace/Review/types";
import { type ShiftPeriod, shiftPeriodToTimeSlot } from "../Shifts/Shift/types";
import {
  EMPLOYMENT_VERIFICATION_STATUS,
  useGetEmploymentVerifications,
} from "./api/useGetEmploymentVerifications";
import { useIsWorkplaceReviewUXUpdateEnabled } from "./hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { PostShiftReviewFlowContent } from "./PostShiftReviewFlowContent";
import {
  type ComparisonPairData,
  type ComparisonSelection,
  LEAVE_REVIEW_ENTRYPOINT,
  type PostShiftReviewFlowProps,
  type PostShiftReviewFormValues,
  type QuestionMappings,
  QuestionTag,
} from "./types";

const nestValidationErrorSchema = z.object({ message: z.array(z.string()) });
const OFFENSIVE_WORDS_PATTERN = /should not contain offensive words/i;

/** API returns uppercase ("AM"), app uses lowercase TimeSlot enum ("am") */
function apiTimeSlotToTimeSlot(value?: string): TimeSlot | undefined {
  if (!value) {
    return undefined;
  }

  return shiftPeriodToTimeSlot[value.toLowerCase() as ShiftPeriod];
}

function buildComparisonPair(
  comparisonPairData: GetComparisonPairResponse | undefined,
  facility: { name: string; address?: string }
): ComparisonPairData | undefined {
  if (!comparisonPairData?.data) {
    return undefined;
  }

  const { relationships } = comparisonPairData.data;
  const included = comparisonPairData.included ?? [];

  const workplaceAId = relationships.workplaceA.data.id;
  const workplaceBId = relationships.workplaceB.data.id;
  const pairShiftId = relationships.shift.data?.id ?? null;

  const findIncluded = (id: string) => included.find((item) => item.id === id);
  const workplaceAData = findIncluded(workplaceAId);
  const workplaceBData = findIncluded(workplaceBId);

  // workplaceB data is required — without it we'd POST blank names
  if (!workplaceBData) {
    return undefined;
  }

  return {
    workplaceA: {
      workplaceId: workplaceAId,
      workplaceName: workplaceAData?.attributes.name ?? facility.name,
      timeSlot: apiTimeSlotToTimeSlot(workplaceAData?.attributes.timeSlot),
      lastWorkedAt: workplaceAData?.attributes.lastWorkedAt,
      address: facility.address,
    },
    workplaceB: {
      workplaceId: workplaceBId,
      workplaceName: workplaceBData.attributes.name,
      timeSlot: apiTimeSlotToTimeSlot(workplaceBData.attributes.timeSlot),
      lastWorkedAt: workplaceBData.attributes.lastWorkedAt,
    },
    shiftId: pairShiftId,
  };
}

function createQuestionAnswerWithOptions(
  answer: string,
  otherText: string | undefined,
  questionId: string,
  options: Array<{ id: string; displayText: string }>
) {
  const selectedOption = options.find((option) => option.displayText === answer);
  const answerData: { questionId: string; text: string; optionId?: string } = {
    questionId,
    text: answer === "Other" ? (otherText ?? "") : answer,
  };

  if (selectedOption?.id) {
    answerData.optionId = selectedOption.id;
  }

  return answerData;
}

export function PostShiftReviewFlow(props: PostShiftReviewFlowProps) {
  const {
    modalState,
    facility,
    onComplete,
    shiftId,
    isDelayedReview,
    source,
    originatingWorkplaceId,
  } = props;

  const { isEnabled, questionFiltersToBeShownWhenEnabled, questionFiltersToBeHiddenWhenDisabled } =
    useIsWorkplaceReviewUXUpdateEnabled();
  const { userId: workerId } = useDefinedWorker();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const workplaceCommentsPrefix = `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workplaces/`;

  const { data: employmentVerifications, isLoading: isEmploymentVerificationsLoading } =
    useGetEmploymentVerifications(
      {
        filter: { workerId, workplaceId: facility.id },
        page: { size: 1 },
      },
      { enabled: isEnabled }
    );

  const employmentVerification = employmentVerifications?.data.at(0);

  const isEmploymentVerificationRejected =
    employmentVerification?.attributes.status === EMPLOYMENT_VERIFICATION_STATUS.REJECTED;

  const hasEmploymentVerification =
    isDefined(employmentVerification) &&
    !isEmploymentVerificationRejected &&
    !employmentVerification.attributes.isExpired;

  const canSubmitEmploymentVerification =
    (source === LEAVE_REVIEW_ENTRYPOINT.WORKPLACE_PROFILE ||
      source === LEAVE_REVIEW_ENTRYPOINT.UNLOCK_REVIEWS) &&
    !hasEmploymentVerification;

  // eg: In case user leaves review from review page, then there is no shiftId to tie the review to
  const shouldFetchShiftData = isDefined(shiftId) && isDelayedReview === true;
  // Fetch shift data for delayed review (used for time slot pill)
  const { data: shiftData, isLoading: isShiftLoading } = useGetWorkerShift(shiftId ?? "", {
    enabled: shouldFetchShiftData,
  });

  // Fetch comparison pair for workplace comparison step (only when flag is on)
  const shouldFetchComparisonPair = isEnabled;
  const { data: comparisonPairData, isLoading: isComparisonPairLoading } = useGetComparisonPair(
    { workplaceId: facility.id, shiftId },
    { enabled: shouldFetchComparisonPair }
  );

  const comparisonPair = buildComparisonPair(comparisonPairData, facility);

  const {
    data: questionsData,
    isLoading,
    error,
  } = useGetQuestions({
    workplaceType: facility.type,
    workplaceId: facility.id,
  });

  const isError = Boolean(error);

  const submitRatingMutation = usePostRating(facility.id, {
    onSuccess: (response) => {
      logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_RATING_SUBMITTED, {
        workplaceId: facility.id,
        rating: response.data.attributes.rating,
        shiftId,
        source,
        ...(isDefined(originatingWorkplaceId) && { originatingWorkplaceId }),
      });
    },
    onError: () => {
      showErrorToast("Failed to submit rating. Please try again.");
    },
  });

  const submitReviewMutation = usePostReview(facility.id);

  const reviewSubmittedRef = useRef(false);

  useEffect(() => {
    if (modalState.modalIsOpen) {
      reviewSubmittedRef.current = false;
    }
  }, [modalState.modalIsOpen]);

  const { mutate: postComparison } = usePostWorkplaceComparison();

  function handleSubmitComparison(selection: ComparisonSelection) {
    if (!comparisonPair) {
      return;
    }

    const commonFields = {
      shiftId: comparisonPair.shiftId ?? undefined,
      workplaceAId: comparisonPair.workplaceA.workplaceId,
      workplaceAName: comparisonPair.workplaceA.workplaceName,
      workplaceBId: comparisonPair.workplaceB.workplaceId,
      workplaceBName: comparisonPair.workplaceB.workplaceName,
    };

    if (selection === "tie") {
      postComparison({ ...commonFields, selectedWorkplaceId: null, isTie: true });
      return;
    }

    postComparison({
      ...commonFields,
      selectedWorkplaceId: comparisonPair[selection].workplaceId,
      isTie: false,
    });
  }

  const filtersEnabledKey = JSON.stringify(questionFiltersToBeShownWhenEnabled);
  const filtersDisabledKey = JSON.stringify(questionFiltersToBeHiddenWhenDisabled);

  const questionMappings = useMemo(() => {
    const supportedTags = [
      QuestionTag.SOFTWARE,
      QuestionTag.SUPPLIES,
      QuestionTag.PATIENTS_PER_NURSE,
      QuestionTag.PARKING,
      QuestionTag.CLEANLINESS,
      QuestionTag.SUPPLY_RATING,
      QuestionTag.CULTURE,
      QuestionTag.PATIENT_CARE,
    ];

    const mappings: QuestionMappings = {
      commentQuestionId: undefined,
      tagToQuestionId: {},
      tagToQuestionText: {},
      questionOptions: {},
      questionNumberConfig: {},
      questionTypes: {},
      ratingQuestions: [],
    };

    if (!questionsData?.data) {
      return mappings;
    }

    const commentQuestion = questionsData.data.find(
      (question) => question.attributes.type === ReviewQuestionType.COMMENT
    );
    if (commentQuestion) {
      mappings.commentQuestionId = commentQuestion.id;
    }

    const activeFilters = isEnabled
      ? questionFiltersToBeShownWhenEnabled
      : questionFiltersToBeHiddenWhenDisabled;
    const hasFilters = activeFilters.length > 0;

    let tagsToIterate: QuestionTag[];
    if (isEnabled && hasFilters) {
      // WHITELIST: iterate only whitelisted tags, in order
      const seen = new Set<QuestionTag>();
      tagsToIterate = [];
      for (const { tag } of activeFilters) {
        if (supportedTags.includes(tag as QuestionTag) && !seen.has(tag as QuestionTag)) {
          seen.add(tag as QuestionTag);
          tagsToIterate.push(tag as QuestionTag);
        }
      }
    } else {
      // BLOCKLIST or no filters: iterate all supported tags
      tagsToIterate = supportedTags;
    }

    const filterPairSet = hasFilters
      ? new Set(activeFilters.map((q) => `${q.tag}:${q.type}`))
      : undefined;
    const allowedPairs = isEnabled ? filterPairSet : undefined;
    const blockedPairs = !isEnabled ? filterPairSet : undefined;

    const questions = questionsData.data;

    function populateMappings(
      tags: QuestionTag[],
      allowed: Set<string> | undefined,
      blocked: Set<string> | undefined
    ) {
      for (const tag of tags) {
        // Find non-RATING question for this tag
        const matchingQuestion = questions.find(
          (question) =>
            question.attributes.type !== ReviewQuestionType.RATING &&
            (allowed?.has(`${tag}:${question.attributes.type}`) ?? true) &&
            !(blocked?.has(`${tag}:${question.attributes.type}`) ?? false) &&
            question.attributes.tags?.some((tagInfo) => tagInfo.tag === tag)
        );

        if (matchingQuestion && !mappings.tagToQuestionId[tag]) {
          mappings.tagToQuestionId[tag] = matchingQuestion.id;
          mappings.tagToQuestionText[tag] = matchingQuestion.attributes.question;
          mappings.questionTypes[matchingQuestion.id] = matchingQuestion.attributes.type;

          if (matchingQuestion.attributes.options?.length > 0) {
            mappings.questionOptions[matchingQuestion.id] = matchingQuestion.attributes.options.map(
              (opt) => ({
                id: opt.id,
                displayText: opt.displayText,
              })
            );
          }

          // Store number config for NUMBER type questions (minValue and maxValue are always defined for NUMBER type)
          if (
            matchingQuestion.attributes.type === ReviewQuestionType.NUMBER &&
            isDefined(matchingQuestion.attributes.minValue) &&
            isDefined(matchingQuestion.attributes.maxValue)
          ) {
            mappings.questionNumberConfig[matchingQuestion.id] = {
              minValue: matchingQuestion.attributes.minValue,
              maxValue: matchingQuestion.attributes.maxValue,
            };
          }
        }

        // Find RATING question for this tag
        const ratingQuestion = questions.find(
          (question) =>
            question.attributes.type === ReviewQuestionType.RATING &&
            (allowed?.has(`${tag}:${question.attributes.type}`) ?? true) &&
            !(blocked?.has(`${tag}:${question.attributes.type}`) ?? false) &&
            question.attributes.tags?.some((tagInfo) => tagInfo.tag === tag)
        );

        if (ratingQuestion) {
          mappings.ratingQuestions.push({
            questionId: ratingQuestion.id,
            questionText: ratingQuestion.attributes.question,
            tag,
          });
        }
      }
    }

    populateMappings(tagsToIterate, allowedPairs, blockedPairs);

    const hasNoQuestions =
      Object.keys(mappings.tagToQuestionId).length === 0 && mappings.ratingQuestions.length === 0;

    if (hasNoQuestions && hasFilters) {
      populateMappings(supportedTags, undefined, undefined);
      mappings.orderedTags = supportedTags;
    } else {
      mappings.orderedTags = tagsToIterate;
    }

    return mappings;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Using serialized keys for stable memoization
  }, [questionsData, isEnabled, filtersEnabledKey, filtersDisabledKey]);

  async function handleSubmitRating(rating: number) {
    await submitRatingMutation.mutateAsync({ rating });
  }

  function addQuestionAnswers(
    formData: PostShiftReviewFormValues,
    answers: PostReviewRequest["answers"]
  ) {
    if (formData.softwareAnswer && questionMappings.tagToQuestionId[QuestionTag.SOFTWARE]) {
      const questionId = questionMappings.tagToQuestionId[QuestionTag.SOFTWARE];
      const options = questionMappings.questionOptions[questionId] || [];
      answers.push(
        createQuestionAnswerWithOptions(
          formData.softwareAnswer,
          formData.softwareOtherText,
          questionId,
          options
        )
      );
    }

    if (formData.suppliesAnswer && questionMappings.tagToQuestionId[QuestionTag.SUPPLIES]) {
      const questionId = questionMappings.tagToQuestionId[QuestionTag.SUPPLIES];
      const options = questionMappings.questionOptions[questionId] || [];
      answers.push(
        createQuestionAnswerWithOptions(formData.suppliesAnswer, undefined, questionId, options)
      );
    }

    if (
      formData.patientCountAnswer &&
      questionMappings.tagToQuestionId[QuestionTag.PATIENTS_PER_NURSE]
    ) {
      const questionId = questionMappings.tagToQuestionId[QuestionTag.PATIENTS_PER_NURSE];
      const options = questionMappings.questionOptions[questionId] || [];
      answers.push(
        createQuestionAnswerWithOptions(formData.patientCountAnswer, undefined, questionId, options)
      );
    }

    if (
      formData.parkingAnswer !== undefined &&
      questionMappings.tagToQuestionId[QuestionTag.PARKING]
    ) {
      const questionId = questionMappings.tagToQuestionId[QuestionTag.PARKING];
      answers.push({
        questionId,
        text: formData.parkingAnswer,
      });
    }

    // Add rating question answers
    if (formData.ratingAnswers) {
      for (const [questionId, ratingValue] of Object.entries(formData.ratingAnswers)) {
        if (ratingValue !== undefined && ratingValue !== "") {
          answers.push({
            questionId,
            text: ratingValue,
          });
        }
      }
    }
  }

  async function handleSubmitReview(formData: PostShiftReviewFormValues) {
    const answers: PostReviewRequest["answers"] = [];
    const isMediaReview = formData.reviewType === "VIDEO";

    // For text reviews, include the review text in answers
    if (!isMediaReview && formData.reviewText.trim() && questionMappings.commentQuestionId) {
      answers.push({
        questionId: questionMappings.commentQuestionId,
        text: formData.reviewText,
      });
    }

    // For video reviews, include the media data directly in answers
    if (isMediaReview && formData.mediaFileKey && questionMappings.commentQuestionId) {
      answers.push({
        questionId: questionMappings.commentQuestionId,
        type: "VIDEO",
        mediaFileKey: formData.mediaFileKey,
        mediaMimeType: formData.mediaMimeType,
        mediaDurationSeconds: formData.mediaDurationSeconds,
        mediaFileSizeBytes: formData.mediaFileSizeBytes,
      });
    }

    addQuestionAnswers(formData, answers);

    // Skip review mutation on retry if it already succeeded (prevents duplicate reviews)
    if (answers.length > 0 && !reviewSubmittedRef.current) {
      await submitReviewMutation.mutateAsync({
        anonymous: formData.isAnonymous ?? false,
        answers,
        employmentVerificationFile: formData.verificationDocument,
      });
      reviewSubmittedRef.current = true;
      logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_REVIEW_SUBMITTED, {
        workplaceId: facility.id,
        shiftId,
        source,
        ...(isMediaReview ? { reviewType: "media" } : {}),
        ...(isDefined(originatingWorkplaceId) && { originatingWorkplaceId }),
      });

      void queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey.some(
            (key) =>
              typeof key === "string" &&
              key.startsWith(workplaceCommentsPrefix) &&
              key.endsWith("/comments")
          );
        },
      });
    }
  }

  function handleSubmitReviewError(error: unknown) {
    if (isAxiosError(error)) {
      const parsed = nestValidationErrorSchema.safeParse(error.response?.data);
      if (
        parsed.success &&
        parsed.data.message.some((message) => OFFENSIVE_WORDS_PATTERN.test(message))
      ) {
        logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_REVIEW_REJECTED_OFFENSIVE_WORDS, {
          workplaceId: facility.id,
          shiftId,
          source,
          ...(isDefined(originatingWorkplaceId) && { originatingWorkplaceId }),
        });
        showErrorToast(
          "Your review contains inappropriate language. Please revise your review and try again."
        );
        return;
      }
    }

    showErrorToast("Failed to submit review. Please try again.");
  }

  const combinedIsLoading =
    isLoading ||
    (shouldFetchShiftData && isShiftLoading) ||
    (shouldFetchComparisonPair && isComparisonPairLoading) ||
    (isEnabled && isEmploymentVerificationsLoading);

  return (
    <PostShiftReviewFlowContent
      modalState={modalState}
      facility={facility}
      shiftId={shiftId}
      questionMappings={questionMappings}
      isLoading={combinedIsLoading}
      isError={isError}
      isDelayedReview={isDelayedReview}
      shift={shiftData?.response}
      comparisonPair={comparisonPair}
      canSubmitEmploymentVerification={canSubmitEmploymentVerification}
      source={source}
      onComplete={onComplete}
      onSubmitRating={(rating) => handleSubmitRating(rating)}
      onSubmitReview={(formData) => handleSubmitReview(formData)}
      onSubmitReviewError={(error) => handleSubmitReviewError(error)}
      onSubmitComparison={(selection) => handleSubmitComparison(selection)}
    />
  );
}
/* eslint-enable max-lines */
